import React from "react";
import { Button } from "antd";
import { STATUS } from "../../shellTokens";
import { Dot, dotColor, fmtBDT, Mono } from "./orderFormShared";

/**
 * Progress signal for the order form.
 *
 * The redesign keeps the form as one scroll rather than a wizard — order entry
 * is non-linear, and operators backtrack constantly — so progress lives here
 * instead of in step headers. Three presentations of the same state:
 *
 *   desktop (≥1180px)  sticky sidebar card
 *   tablet  (641–1179) horizontal strip below the form
 *   mobile  (≤640px)   one-line status bar docked above the total bar
 *
 * Section rows are buttons: clicking one focuses the first offending field in
 * that section, which is what replaces the old top-of-page error Alert.
 */

const Rule = ({ margin = "14px 0" }) => (
  <div style={{ height: 1, background: "var(--border)", margin }} />
);

const TotalRow = ({ label, value, strong }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
    <span
      style={{
        font: strong ? "600 13px Outfit, sans-serif" : "400 12.5px Outfit, sans-serif",
        color: strong ? "var(--text)" : "var(--muted)",
      }}
    >
      {label}
    </span>
    <Mono
      style={{
        fontSize: strong ? 21 : 13,
        fontWeight: 700,
        color: strong ? "var(--text)" : "var(--muted)",
      }}
    >
      {fmtBDT(value)}
    </Mono>
  </div>
);

const SectionRow = ({ section, onJump }) => {
  const complete = section.done >= section.total;
  return (
    <button
      type="button"
      onClick={() => onJump(section.key)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "9px 10px",
        borderRadius: 9,
        border: "none",
        textAlign: "left",
        cursor: "pointer",
        background: section.errors ? "rgba(248,113,113,.08)" : "transparent",
        color: "inherit",
        fontFamily: "inherit",
      }}
      className="et-navitem"
    >
      <Dot color={dotColor(section)} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            font: "600 12.5px Outfit, sans-serif",
            color: complete || section.errors ? "var(--text)" : "var(--muted)",
          }}
        >
          {section.label}
        </span>
        <span
          style={{
            display: "block",
            font: "400 11px Outfit, sans-serif",
            color: section.errors ? STATUS.errorSoft : "var(--dim)",
          }}
        >
          {section.errors
            ? `${section.errors} to fix — tap to jump`
            : section.caption}
        </span>
      </span>
      {section.amount != null && (
        <Mono style={{ fontSize: 12, fontWeight: 700, color: "var(--dim)" }}>
          {fmtBDT(section.amount)}
        </Mono>
      )}
    </button>
  );
};

const StatusChip = ({ section, onJump }) => {
  const color = dotColor(section);
  const active = section.errors || section.done > 0;
  return (
    <button
      type="button"
      onClick={() => onJump(section.key)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        flex: "none",
        padding: "5px 10px",
        borderRadius: 7,
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        background: section.errors
          ? "rgba(248,113,113,.12)"
          : active
            ? "rgba(251,191,36,.12)"
            : "var(--surface2)",
      }}
    >
      <Dot color={color} size={6} />
      <span
        style={{
          font: "600 11.5px Outfit, sans-serif",
          color: section.errors ? STATUS.errorSoft : active ? STATUS.warn : "var(--muted)",
          whiteSpace: "nowrap",
        }}
      >
        {section.label}
      </span>
    </button>
  );
};

const OrderFormRail = ({
  mode,
  sections,
  done,
  total,
  percent,
  orderAmount,
  deliveryCharge,
  grandTotal,
  submitLabel,
  submitting,
  disabled,
  hasErrors,
  onJump,
  draftSavedAt,
  changedCount,
}) => {
  const barColor = hasErrors ? STATUS.error : percent >= 100 ? STATUS.ok : STATUS.warn;

  const submitButton = (extraProps = {}) => (
    <Button
      type="primary"
      htmlType="submit"
      loading={submitting}
      disabled={disabled}
      {...extraProps}
    >
      {submitLabel}
    </Button>
  );

  const footnote =
    mode === "edit"
      ? changedCount > 0
        ? `${changedCount} field${changedCount === 1 ? "" : "s"} changed since opening`
        : "No changes yet"
      : draftSavedAt
        ? `Draft saved ${draftSavedAt} · restores if the tab closes`
        : "Draft saves as you type";

  return (
    <>
      {/* Desktop: sticky sidebar */}
      <aside className="et-of-rail">
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          <header
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ font: "700 14.5px Outfit, sans-serif" }}>Order summary</div>
            <span
              style={{
                font: "700 10.5px Outfit, sans-serif",
                color: mode === "edit" ? "var(--activeText)" : STATUS.warn,
                background: mode === "edit" ? "var(--activeBg)" : "rgba(251,191,36,.12)",
                padding: "3px 8px",
                borderRadius: 6,
              }}
            >
              {mode === "edit" ? "Editing" : "Draft"}
            </span>
          </header>

          <div style={{ padding: "14px 18px 16px" }}>
            <div style={{ font: "600 11.5px Outfit, sans-serif", color: "var(--muted)", marginBottom: 8 }}>
              Readiness
            </div>
            <div
              style={{
                height: 4,
                borderRadius: 2,
                background: "var(--surface2)",
                overflow: "hidden",
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  width: `${percent}%`,
                  height: "100%",
                  background: barColor,
                  transition: "width .2s ease",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                font: "600 11px Outfit, sans-serif",
                color: "var(--dim)",
                marginBottom: 14,
              }}
            >
              <span>{`${done} of ${total} required fields`}</span>
              <Mono>{`${percent}%`}</Mono>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {sections.map((s) => (
                <SectionRow key={s.key} section={s} onJump={onJump} />
              ))}
            </div>

            <Rule />

            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <TotalRow label="Products" value={orderAmount} />
              <TotalRow label="Delivery" value={deliveryCharge} />
              <div style={{ paddingTop: 9, borderTop: "1px solid var(--border)" }}>
                <TotalRow label="Grand total" value={grandTotal} strong />
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              {submitButton({ size: "large", block: true })}
            </div>
            <div
              style={{
                textAlign: "center",
                font: "400 11px Outfit, sans-serif",
                color: "var(--dim)",
                marginTop: 9,
              }}
            >
              {footnote}
            </div>
          </div>
        </div>
      </aside>

      {/* Tablet: the same state as one horizontal strip below the form */}
      <div
        className="et-of-strip"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          padding: 16,
          alignItems: "center",
          gap: 18,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ font: "600 11.5px Outfit, sans-serif", color: "var(--muted)", marginBottom: 8 }}>
            Readiness
          </div>
          <div style={{ height: 4, borderRadius: 2, background: "var(--surface2)", overflow: "hidden" }}>
            <div style={{ width: `${percent}%`, height: "100%", background: barColor }} />
          </div>
          <div style={{ font: "600 11px Outfit, sans-serif", color: "var(--dim)", marginTop: 5 }}>
            {`${done} of ${total} required fields · `}
            {footnote}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {sections.map((s) => (
            <StatusChip key={s.key} section={s} onJump={onJump} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
          <div>
            <div style={{ font: "400 11px Outfit, sans-serif", color: "var(--dim)" }}>Grand total</div>
            <Mono style={{ fontSize: 21, fontWeight: 700 }}>{fmtBDT(grandTotal)}</Mono>
          </div>
          {submitButton()}
        </div>
      </div>

      {/* Mobile: status line docked above the total bar */}
      <div
        className="et-of-dock"
        style={{
          position: "sticky",
          bottom: 0,
          zIndex: 20,
          margin: "4px -16px 0",
          background: "var(--surface)",
          borderTop: "1px solid var(--border)",
        }}
      >
        <div
          style={{
            padding: "8px 16px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div className="et-of-dock__chips">
            {sections.map((s) => (
              <StatusChip key={s.key} section={s} onJump={onJump} />
            ))}
          </div>
          <Mono style={{ flex: "none", fontSize: 11, fontWeight: 600, color: "var(--dim)" }}>
            {`${done}/${total}`}
          </Mono>
        </div>
        <div style={{ padding: "12px 16px 14px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: "400 11.5px Outfit, sans-serif", color: "var(--dim)" }}>Grand total</div>
            <Mono style={{ fontSize: 22, fontWeight: 700 }}>{fmtBDT(grandTotal)}</Mono>
          </div>
          {submitButton({ size: "large" })}
        </div>
      </div>
    </>
  );
};

export default OrderFormRail;
